import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../api/axiosBaseQuery';

// Types aligned with services/chatService.ts
export type Tag = {
  id: number;
  name: string;
  color?: string | null;
  folder_id?: number | null;
};

export type TagFolder = {
  id: number;
  name: string;
  parent_id?: number | null;
};

type TagsWithFolders = { tags: Tag[]; folders: TagFolder[] };

function ck() {
  try {
    return localStorage.getItem('comercio_key') || null;
  } catch {
    return null;
  }
}

export const tagsApi = createApi({
  reducerPath: 'tagsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Tags', 'Folders', 'ThreadTags'],
  endpoints: (builder) => ({
    // Lists
    getTagsWithFolders: builder.query<TagsWithFolders, void>({
      query: () => ({
        url: '/chat/tags',
        method: 'GET',
        params: {
          include_folders: true,
          ...(ck() ? { comercio_key: ck() } : {}),
          ts: Date.now(),
        },
      }),
      transformResponse: (data: any): TagsWithFolders => {
        if (!data || typeof data !== 'object') {
          return { tags: [], folders: [] };
        }
        if (Array.isArray(data.tags) || Array.isArray(data.folders)) {
          return {
            tags: Array.isArray(data.tags) ? data.tags : [],
            folders: Array.isArray(data.folders) ? data.folders : [],
          };
        }
        // Fallback legacy shape
        const tags = Array.isArray(data.data) ? (data.data as Tag[]) : [];
        return { tags, folders: [] };
      },
      providesTags: ['Tags', 'Folders'],
    }),

    getThreadTags: builder.query<Tag[], { threadId: string }>({
      query: ({ threadId }) => {
        const parts: string[] = [];
        const ckVal = ck();
        if (ckVal) parts.push(`comercio_key=${encodeURIComponent(ckVal)}`);
        try {
          const jwt = typeof localStorage !== 'undefined' ? localStorage.getItem('jwtToken') : '';
          if (jwt && jwt.trim() !== '') parts.push(`token=${encodeURIComponent(jwt)}`);
        } catch {}
        parts.push(`ts=${Date.now()}`);
        const qs = parts.length ? `?${parts.join('&')}` : '';
        return {
          url: `/chat/chat-sessions/${encodeURIComponent(threadId)}/tags${qs}`,
          method: 'GET',
        };
      },
      transformResponse: (data: any): Tag[] => {
        if (!data || typeof data !== 'object') return [];
        if (Array.isArray(data.tags)) return data.tags;
        if (Array.isArray(data.data)) return data.data;
        return [];
      },
      providesTags: (result, _err, arg) =>
        Array.isArray(result)
          ? [{ type: 'ThreadTags', id: arg.threadId }, 'Tags']
          : [{ type: 'ThreadTags', id: arg.threadId }, 'Tags'],
    }),

    getThreadTagsBulk: builder.query<Record<string, Tag[]>, { threadIds: string[] }>({
      query: ({ threadIds }) => {
        const ids = (Array.isArray(threadIds) ? threadIds : []).filter(Boolean).map(String);
        const parts: string[] = [];
        for (const id of ids) {
          parts.push(`thread_ids[]=${encodeURIComponent(id)}`);
        }
        const ckVal = ck();
        if (ckVal) parts.push(`comercio_key=${encodeURIComponent(ckVal)}`);
        try {
          const jwt = typeof localStorage !== 'undefined' ? localStorage.getItem('jwtToken') : '';
          if (jwt && jwt.trim() !== '') parts.push(`token=${encodeURIComponent(jwt)}`);
        } catch {}
        parts.push(`ts=${Date.now()}`);
        const qs = parts.length ? `?${parts.join('&')}` : '';
        return {
          url: `/chat/thread-tags${qs}`,
          method: 'GET',
        };
      },
      transformResponse: (data: any): Record<string, Tag[]> => {
        if (!data || typeof data !== 'object') return {};
        const mapping: Record<string, Tag[]> = data.mapping || data.map || data.data || {};
        return mapping || {};
      },
      providesTags: ['Tags'],
    }),

    // Thread tag assignment
    addTagToThread: builder.mutation<
      { success?: boolean },
      { threadId: string; tag_id?: number; tag_name?: string; color?: string | null; folder_id?: number | null }
    >({
      query: ({ threadId, ...payload }) => ({
        url: `/chat/chat-sessions/${encodeURIComponent(threadId)}/tags`,
        method: 'POST',
        data: { comercio_key: ck() || undefined, ...payload },
      }),
      invalidatesTags: (_res, _err, arg) => [{ type: 'ThreadTags', id: arg.threadId }, 'Tags'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const bc = new BroadcastChannel('chat-tags');
            bc.postMessage({ type: 'tags-changed' });
          } catch {}
          try {
            localStorage.setItem('Tags:ping', String(Date.now()));
          } catch {}
        } catch {}
      },
    }),

    removeTagFromThread: builder.mutation<{ success?: boolean }, { threadId: string; tagId: number }>({
      query: ({ threadId, tagId }) => ({
        url: `/chat/chat-sessions/${encodeURIComponent(threadId)}/tags/${tagId}`,
        method: 'DELETE',
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      invalidatesTags: (_res, _err, arg) => [{ type: 'ThreadTags', id: arg.threadId }, 'Tags'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const bc = new BroadcastChannel('chat-tags');
            bc.postMessage({ type: 'tags-changed' });
          } catch {}
          try {
            localStorage.setItem('Tags:ping', String(Date.now()));
          } catch {}
        } catch {}
      },
    }),

    // CRUD tags
    createTag: builder.mutation<Tag | null, { name: string; color?: string | null; folder_id?: number | null }>({
      query: (payload) => ({
        url: '/chat/tags',
        method: 'POST',
        data: { comercio_key: ck() || undefined, ...payload },
      }),
      transformResponse: (data: any): Tag | null => data?.tag || data?.data || null,
      invalidatesTags: ['Tags'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const bc = new BroadcastChannel('chat-tags');
            bc.postMessage({ type: 'tags-changed' });
          } catch {}
          try {
            localStorage.setItem('Tags:ping', String(Date.now()));
          } catch {}
        } catch {}
      },
    }),

    updateTag: builder.mutation<{ success?: boolean }, { id: number; name?: string; color?: string | null; folder_id?: number | null }>({
      query: ({ id, ...patch }) => ({
        url: `/chat/tags/${id}`,
        method: 'PUT',
        data: patch,
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      invalidatesTags: ['Tags'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const bc = new BroadcastChannel('chat-tags');
            bc.postMessage({ type: 'tags-changed' });
          } catch {}
          try {
            localStorage.setItem('Tags:ping', String(Date.now()));
          } catch {}
        } catch {}
      },
    }),

    deleteTag: builder.mutation<{ success?: boolean }, { id: number }>({
      query: ({ id }) => ({
        url: `/chat/tags/${id}`,
        method: 'DELETE',
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      invalidatesTags: ['Tags'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const bc = new BroadcastChannel('chat-tags');
            bc.postMessage({ type: 'tags-changed' });
          } catch {}
          try {
            localStorage.setItem('Tags:ping', String(Date.now()));
          } catch {}
        } catch {}
      },
    }),

    // CRUD folders
    createFolder: builder.mutation<TagFolder | null, { name: string; parent_id?: number | null }>({
      query: (input) => ({
        url: '/chat/tag-folders',
        method: 'POST',
        data: { comercio_key: ck() || undefined, ...input },
      }),
      transformResponse: (data: any): TagFolder | null => data?.folder || data?.data || null,
      invalidatesTags: ['Folders', 'Tags'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const bc = new BroadcastChannel('chat-tags');
            bc.postMessage({ type: 'tags-changed' });
          } catch {}
          try {
            localStorage.setItem('Tags:ping', String(Date.now()));
          } catch {}
        } catch {}
      },
    }),

    updateFolder: builder.mutation<{ success?: boolean }, { id: number; name?: string; parent_id?: number | null }>({
      query: ({ id, ...patch }) => ({
        url: `/chat/tag-folders/${id}`,
        method: 'PUT',
        data: patch,
        params: ck() ? { comercio_key: ck() } : undefined,
      }),
      invalidatesTags: ['Folders', 'Tags'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const bc = new BroadcastChannel('chat-tags');
            bc.postMessage({ type: 'tags-changed' });
          } catch {}
          try {
            localStorage.setItem('Tags:ping', String(Date.now()));
          } catch {}
        } catch {}
      },
    }),

    deleteFolder: builder.mutation<{ success?: boolean }, { id: number; move_to_id?: number | null }>({
      query: ({ id, move_to_id }) => ({
        url: `/chat/tag-folders/${id}`,
        method: 'DELETE',
        params: {
          ...(ck() ? { comercio_key: ck() } : {}),
          ...(move_to_id !== undefined ? { move_to_id } : {}),
        },
      }),
      invalidatesTags: ['Folders', 'Tags'],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const bc = new BroadcastChannel('chat-tags');
            bc.postMessage({ type: 'tags-changed' });
          } catch {}
          try {
            localStorage.setItem('Tags:ping', String(Date.now()));
          } catch {}
        } catch {}
      },
    }),
  }),
});

export const {
  useGetTagsWithFoldersQuery,
  useGetThreadTagsQuery,
  useLazyGetThreadTagsBulkQuery,
  useAddTagToThreadMutation,
  useRemoveTagFromThreadMutation,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useCreateFolderMutation,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
} = tagsApi;
